import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';

/**
 * Checks if an email is fat fingered and returns suggested email
 * For example hello.world@gamil.com is suggested as hello.world@gmail.com
 * @param {string} email Email string
 * @param {object} fatFingerRegEx fatfinger regex
 */
export const checkForEmailFatFingeringAndGetSuggestedEmail = (
    email,
    fatFingerRegEx = [
        { domain: 'gmail.com', regex: '@g.*[m|a|i|l].*$' },
        { domain: 'yahoo.com', regex: '@[y|h][a|h].*[o|0]\\..*$' },
        { domain: 'hotmail.com', regex: '@h.*il\\..*$' },
        { domain: 'icloud.com', regex: '@ic.*d\\..*$' },
        { domain: 'aol.com', regex: '@[a-z]o[a-z]\\..*$' },
        { domain: 'outlook.com', regex: '@[o|0]u.*[o|0]k\\..*$' },
    ]
) => {
    const returnObj = {
        isFatFingered: false,
        suggestedEmail: email,
    };

    try {
        fatFingerRegEx.some((entry) => {
            const { domain, regex } = entry;
            const domainRegEx = new RegExp(regex, 'gi');
            const domainMatched = domainRegEx.test(email);
            if (domainMatched) {
                const suggestedEmail = email.replace(domainRegEx, `@${domain}`);
                if (suggestedEmail !== email) {
                    returnObj.isFatFingered = true;
                    returnObj.suggestedEmail = suggestedEmail;
                }
            }
            return domainMatched;
        });
    } catch (err) {
        // supressed
    }

    return returnObj;
};

/**
 * Checks error code and message and return new UI message from preference
 */
export const getUIErrorMessage = (preferences = {}, errorCode = '', errorMsg = '') => {
    const {
        signin: { serviceError },
    } = preferences;

    return (
        errorCode &&
        errorMsg &&
        serviceError &&
        serviceError.find(
            (error) => error.errorCode === errorCode && errorMsg.includes(error.errorMessage)
        )
    );
};

/**
 * Check whether phone or credit card field
 * @param enableInputFieldMaskFix
 * @param fieldName
 * @param inputType
 * @param value
 */
export const getMaskEligibleInfo = (enableInputFieldMaskFix, fieldName, type, value) => {
    if (enableInputFieldMaskFix.includes(fieldName)) {
        if (['phoneNumber', 'phone'].includes(fieldName) && type === 'tel') {
            return {
                enableInputFieldMask: true,
                inputType: 'phone',
            };
        }
        if (fieldName === 'creditCardNumber' && type === 'tel') {
            const isCreditCardEditField = value && /\*/g.test(value); // The credit card value will be something like '****8888' during the edit.
            return {
                enableInputFieldMask: !isCreditCardEditField,
                inputType: 'creditCard',
            };
        }
    }
    return {};
};

/**
 * Handle data preparation for eligible input field on key down event
 * @param e
 * @param enableInputFieldMask
 * @param inputType
 */
export const getInputKeyDownInfo = (e, enableInputFieldMask, inputType) => {
    const { key = '', keyCode = '', target = {}, target: { selectionStart } = {} } = e;
    const supportInputFieldMask = enableInputFieldMask && key && 'selectionStart' in target;
    if (supportInputFieldMask) {
        return {
            keyCode,
            supportInputFieldMask,
            inputType,
            selectionStart,
        };
    }
    return {};
};

/**
 * Handle cursor position on input key up event
 * @param event
 * @param inputKeyEvents
 */
export const handleCursorPosition = (event, inputKeyEvents) => {
    const { selectionStart = '', inputType = '' } = inputKeyEvents;
    const { target = {}, key = '' } = event;

    if (
        ['phone', 'creditCard'].includes(inputType) &&
        selectionStart >= 0 &&
        key &&
        /\d/.test(key)
    ) {
        let curPosition = selectionStart + 1;
        if (inputType === 'phone') {
            // Increment cursor position if it not numeric digit (xxx) xxx-xxxx
            if ([1, 6, 9, 10].includes(curPosition)) {
                curPosition += 1;
            } else if ([4, 5].includes(curPosition)) {
                curPosition += 2;
            }
        } else if (inputType === 'creditCard') {
            // Increment cursor position if it not numeric digit xxxx xxxx xxxx xxxx
            if ([5, 10, 15].includes(curPosition)) {
                curPosition += 1;
            }
        }
        target && target.setSelectionRange(curPosition, curPosition);
    }
};

/**
 * Check whether delete or backspace key triggered
 * @param inputType
 * @param InputKeyEvents
 */
export const isInputDelKeyTriggered = (inputType, InputKeyEvents) => {
    const { keyCode = '' } = InputKeyEvents;
    return (
        inputType &&
        ['phone', 'creditCard'].includes(inputType) &&
        keyCode &&
        [8, 46].includes(keyCode)
    );
};

/**
 * Remove last numberic digit if phone number numeric digit length exceed 10
 * Ex. input format (8981 908-2222 or (898)2908-2222 or 9898) 908-2222 or (898) 90852222
 * @param value
 */
export const getFirstTenDigitOfPhone = (value) => {
    if (value && value.replace(/\D/g, '').length === 11) {
        return value.slice(0, -1);
    }
    return value;
};

export const isQuickScreenEligible = (context = {}) => {
    // returns true - flag ON, loggedInUser with rewardId.

    const {
        featureFlags: { enableQuickScreenBanner = true } = {},
        preferences: { quickScreenConfig: { eligibiltyReCheckTimeBuffer = 80 } = {} } = {},
    } = context;
    const isUserLoggedIn = TokenProvider.get('DP_USER_STATE') === '1';
    const qsEligibilityData = JSON.parse(LocalStorage.getData('qsEligibilityData'));
    if (qsEligibilityData && qsEligibilityData?.isQsNotEligible) {
        const currentTime = Date.now();
        const { lastVerifiedTime } = qsEligibilityData;
        const timeDiff = Math.round((currentTime - lastVerifiedTime) / 1000 / 60);
        if (timeDiff <= eligibiltyReCheckTimeBuffer) {
            return false;
        }
    }
    LocalStorage.removeData('qsEligibilityData');
    if (context?.isNative) {
        return enableQuickScreenBanner && isUserLoggedIn;
    }

    return enableQuickScreenBanner && isUserLoggedIn && !!TokenProvider.get('DP_REWARDS_ID');
};
